import { ValuesCache } from '@leverege/value-cache';
import Constants from '../Constants.js';
const cache = new ValuesCache();
function getLookupString({
  path,
  opts,
  queryName
}) {
  return `${path}|${opts?.startIndex}|${opts?.perPage}|${queryName}`;
}
function getQueryName(qName) {
  return qName || Constants.INTERFACE_DEFAULT_QUERY_NAME;
}

/**
 * gets the status of the given path
 * @param {object} state 
 * @param {string} path 
 * @returns {string} the status
 */
function getStatus(state, path, queryName) {
  const qName = getQueryName(queryName);
  return state.data?.[path]?.[qName]?.status;
}

/**
 * checks if a query is loading
 * @param {object} state the current interface redux object
 * @param {string} path string representing the query path sent to the api
 * @returns {boolean} 
 */
function isLoading(state, path, queryName) {
  return getStatus(state, path, queryName) === Constants.INTERFACE_STATUS_LOADING;
}

/**
 * check if a query is in error
 * @param {object} state the current interface redux object
 * @param {string} path string representing the query path sent to the api
 * @returns {boolean}
 */
function isError(state, path, queryName) {
  return getStatus(state, path, queryName) === Constants.INTERFACE_STATUS_ERROR;
}

/**
 * check if a query has completed
 * @param {object} state the current interface redux object
 * @param {string} path string representing the query path sent to the api
 * @returns {boolean}
 */
function isDone(state, path, queryName) {
  return getStatus(state, path, queryName) === Constants.INTERFACE_STATUS_DONE;
}

/**
 * check if a query result is a list
 * @param {object} state the current interface redux object
 * @param {string} path string representing the query path sent to the api
 * @returns {boolean}
 */
function isList(state, path, opts, queryName) {
  const qName = getQueryName(queryName);
  let data;
  if (opts?.isRoot !== false) {
    data = state.data[path]?.[qName];
  } else {
    data = state;
  }
  return data && data.items && path.indexOf('*') < 0;
}

/**
 * check if a query result is a table
 * @param {object} state the current interface redux object
 * @param {string} path string representing the query path sent to the api
 * @returns {boolean}
 */
function isTable(state, path, opts, queryName) {
  const qName = getQueryName(queryName);
  let data;
  if (opts?.isRoot !== false) {
    data = state.data[path]?.[qName];
  } else {
    data = state;
  }
  return data && data.items && path.indexOf('*') >= 0;
}

/**
 * check if a query result is an object
 * @param {object} state the current interface redux object
 * @param {string} path string representing the query path sent to the api
 * @returns {boolean}
 */
function isObject(state, path, queryName) {
  const qName = getQueryName(queryName);
  const data = state.data[path]?.[qName];
  return data && data.id != null;
}

/**
 * gets the result of a query if it is an object
 * @param {object} state the current interface redux object
 * @param {string} path string representing the query path sent to the api
 * @returns {object?} 
 */
function getObject(state, path, queryName) {
  const qName = getQueryName(queryName);
  const data = state.data[path]?.[qName];
  if (data && data.id) {
    return state.lookup[data.id];
  }
  const split = path.split('/');
  const id = split[split.length - 1];
  const rest = split.slice(0, -1).join('/');
  if (state.reverseLookup?.[id]?.[rest]) {
    return state.lookup[id];
  }
  return null;
}

/**
 * gets the result of a history query
 * @param {object} state the current interface redux object
 * @param {string} path string representing the query path sent to the api
 * @param {string} queryName name of the query  
 * @returns {object}
 */
function getHistory(state, path, queryName, opts) {
  const qName = getQueryName(queryName);
  const entry = state.data[path]?.[qName];
  const data = state.lookup[entry?.id];
  if (!data?.items) {
    return null;
  }
  if (opts?.getAll) {
    return data;
  }
  return cache.calc(getLookupString({
    path,
    queryName: qName,
    opts
  }), calcList, data, opts?.startIndex, opts?.perPage);
}

/**
 * get the list result of a query
 * @param {object} state the current interface redux object
 * @param {string} path string representing the query path sent to the api
 * @param {object} opts 
 * @returns {array} array of items 
 */
function calcList(data, startIndex, perPage) {
  const rtn = {
    ...data
  };
  if (data.items) {
    let items = data.items;
    if (startIndex != null || perPage != null) {
      rtn.startIndex = startIndex || 0;
      rtn.perPage = perPage || data.perPage || 100;
      items = items.slice(rtn.startIndex, rtn.perPage + rtn.startIndex);
    } else if (data?.startIndex != null || data?.perPage != null) {
      rtn.startIndex = data.startIndex || 0;
      rtn.perPage = data.perPage || 100;
      items = items.slice(rtn.startIndex, rtn.perPage + rtn.startIndex);
    } else {
      rtn.count = items.length;
      rtn.startIndex = 0;
      rtn.perPage = data.perPage;
    }
    rtn.items = items;
    return rtn;
  }
  return rtn;
}
/**
 * 
 * @param {object} state 
 * @param {string} path 
 * @param {object} opts 
 * @param {number} opts.startIndex
 * @param {number} opts.perPage
 * @param {boolean} opts.getAll
 * @param {string} queryName 
 */
function getList(state, path, opts, queryName) {
  const qName = getQueryName(queryName);
  const data = state.data[path]?.[qName];
  if (!isList(data, path, {
    isRoot: false
  }, queryName)) {
    return null;
  }
  if (opts?.getAll) {
    return data;
  }
  return cache.calc(getLookupString({
    path,
    queryName: qName,
    opts
  }), calcList, data, opts?.startIndex, opts?.perPage);
}

/**
 * get the table result of a query
 * @param {object} state the current interface redux object
 * @param {string} path string representing the query path sent to the api
 * @param {object} opts 
 * @returns {array} array of items 
 */
function calcTable(data, startIndex, perPage) {
  const rtn = {
    ...data
  };
  if (data.items) {
    let items = data.items;
    if (startIndex != null || perPage != null) {
      rtn.startIndex = startIndex || 0;
      rtn.perPage = perPage || data.perPage || 100;
      items = items.slice(rtn.startIndex, rtn.perPage + rtn.startIndex);
    } else if (data?.startIndex != null || data?.perPage != null) {
      rtn.startIndex = data.startIndex || 0;
      rtn.perPage = data.perPage || 100;
      items = items.slice(rtn.startIndex, rtn.perPage + rtn.startIndex);
    } else {
      rtn.count = items.length;
      rtn.startIndex = 0;
      rtn.perPage = data.perPage;
    }
    return rtn;
  }
  rtn.items = [];
  return rtn;
}
function getTable(state, path, opts, queryName) {
  const qName = getQueryName(queryName);
  const data = state.data[path]?.[qName];
  if (!isTable(data, path, {
    isRoot: false
  }, queryName)) {
    return null;
  }
  if (opts?.getAll) {
    return data;
  }
  return cache.calc(getLookupString({
    path,
    queryName: qName,
    opts
  }), calcTable, data, opts?.startIndex, opts?.perPage);
}
function getError(state, path, queryName) {
  const qName = getQueryName(queryName);
  return state.data?.[path]?.[qName]?.error;
}
export default {
  getStatus,
  isLoading,
  isError,
  getError,
  isDone,
  isList,
  isTable,
  isObject,
  getObject,
  getHistory,
  calcList,
  getList,
  getTable,
  calcTable
};